import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchPlacePredictions } from './googlePlaces.js';

const CustomPlaceAutocomplete = ({ id, onChange, value = '', label = '', error, helperText, isRequired = true, disabled=false }) => {
    const [inputValue, setInputValue] = useState(value || '');
    const [suggestions, setSuggestions] = useState([]);
    const [box, setBox] = useState(null);
    const [open, setOpen] = useState(false);
    const inputRef = useRef(null);

    const updateBox = () => {
        const el = inputRef.current;
        if (!el) {
            return;
        }
        const rect = el.getBoundingClientRect();
        setBox({
            top: rect.bottom + 4,
            left: rect.left,
            width: Math.max(rect.width, 240),
        });
    };

    useEffect(() => {
        setInputValue(value || '');
    }, [value]);

    useEffect(() => {
        if (!open) {
            setSuggestions([]);
            return undefined;
        }
        let cancelled = false;
        const handle = window.setTimeout(async () => {
            const next = await fetchPlacePredictions(inputValue);
            if (!cancelled) {
                setSuggestions(next.suggestions);
                if (next.suggestions.length) {
                    updateBox();
                }
            }
        }, 250);
        return () => {
            cancelled = true;
            window.clearTimeout(handle);
        };
    }, [inputValue, open]);

    const pick = (labelText) => {
        setInputValue(labelText);
        setSuggestions([]);
        onChange?.(labelText);
    };

    return (
        <label className="sm-place-field">
            {label && <span className="sm-place-label">{label}{isRequired ? ' *' : ''}</span>}
            {id &&
                <input
                    type="hidden"
                    id={id}
                    className="e-field"
                    name={id}
                    value={inputValue}
                    readOnly
                />}
            <input
                ref={inputRef}
                id={id ? `${id}Search` : undefined}
                className={`e-input sm-place-input${error ? ' sm-place-input-error' : ''}`}
                value={inputValue}
                disabled={disabled}
                required={isRequired}
                autoComplete="off"
                placeholder="Type 3 or more characters to search"
                onFocus={() => {
                    setOpen(true);
                    updateBox();
                }}
                onBlur={() => {
                    window.setTimeout(() => setOpen(false), 150);
                }}
                onChange={(event) => {
                    const next = event.target.value;
                    setInputValue(next);
                    onChange?.(next);
                }}
            />
            {error && helperText && <span className="sm-place-help">{helperText}</span>}
            {open && suggestions.length > 0 && box && createPortal(
                <ul
                    className="sm-place-suggest"
                    data-testid="place-suggestions"
                    style={{ top: box.top, left: box.left, width: box.width }}
                >
                    {suggestions.map((item) =>
                        <li key={item.id}>
                            <button
                                type="button"
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    pick(item.label);
                                }}
                            >
                                {item.label}
                            </button>
                        </li>)}
                </ul>,
                document.body
            )}
        </label>
    );
};

export default CustomPlaceAutocomplete;
